import { useEffect, useMemo, useState } from "react";
import type { SortDir } from "@/lib/bookingTableUtils";

export function guestInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  if (trimmed.includes(",")) {
    const [last, first] = trimmed.split(",").map((part) => part.trim());
    const letters = `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();
    if (letters) return letters;
  }
  const words = trimmed.split(/\s+/).filter(Boolean);
  const first = words[0];
  const second = words[1];
  if (first && second) return `${first[0] ?? ""}${second[0] ?? ""}`.toUpperCase();
  return trimmed.slice(0, 2).toUpperCase();
}

export function formatPartyLine(booking: {
  adults?: number | null;
  children?: number | null;
  dogs?: number | null;
}): string {
  const adults = booking.adults ?? 0;
  const children = booking.children ?? 0;
  const dogs = booking.dogs ?? 0;
  const parts = [`${adults} Erw.`];
  if (children > 0) parts.push(`${children} Kind.`);
  if (dogs > 0) parts.push(`${dogs} Hund${dogs === 1 ? "" : "e"}`);
  return parts.join(" · ");
}

export function nightsLabel(nights: number): string {
  return nights === 1 ? "1 Nacht" : `${nights} Nächte`;
}

export function matchesStayQuery(query: string, parts: Array<string | null | undefined>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return parts.some((part) => (part ?? "").toLowerCase().includes(q));
}

export interface StaySortState {
  key: string;
  dir: SortDir;
}

export function nextStaySort(current: StaySortState | null, key: string): StaySortState | null {
  if (current?.key !== key) return { key, dir: "asc" };
  if (current.dir === "asc") return { key, dir: "desc" };
  return null;
}

export function sortStayRows<T>(
  rows: T[],
  sort: StaySortState | null,
  value: (row: T, key: string) => string | number,
): T[] {
  if (!sort) return rows;
  const sorted = [...rows];
  sorted.sort((a, b) => {
    const left = value(a, sort.key);
    const right = value(b, sort.key);
    const result =
      typeof left === "number" && typeof right === "number"
        ? left - right
        : String(left).localeCompare(String(right), "de", {
            numeric: true,
            sensitivity: "base",
          });
    return sort.dir === "asc" ? result : -result;
  });
  return sorted;
}

export function useStayListState(listKey: string) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<StaySortState | null>(null);

  useEffect(() => {
    setSearch("");
  }, [listKey]);

  return { search, setSearch, sort, setSort };
}

export function useFilteredStayRows<T>(
  rows: T[],
  search: string,
  sort: StaySortState | null,
  matches: (row: T, query: string) => boolean,
  sortValue: (row: T, key: string) => string | number,
): T[] {
  return useMemo(() => {
    const filtered = rows.filter((row) => matches(row, search));
    return sortStayRows(filtered, sort, sortValue);
  }, [rows, search, sort, matches, sortValue]);
}
